import { Badge, Collapsible, MenuItem } from "@wojo/ui";
import cn from "clsx";
import { createElement, CSSProperties, ReactElement, useId } from "react";
import { IconType } from "react-icons";
import { FiChevronDown } from "react-icons/fi";
import styles from "./NavItem.module.scss";
import { useNavItem } from "./useNavItem";
import Link from "next/link";

export type NavItemKind = "main" | "sub-section" | "page" | "sub-page";

const childKind = (kind: NavItemKind) => {
    switch (kind) {
        case "main":
            return "sub-section";
        case "sub-section":
            return "page";
        case "page":
            return "sub-page";
        case "sub-page":
            return "sub-page";
    }
};

export type ExpandedState = "Expanded" | "Collapsed" | "Default";

export type NavItemProps = {
    /**
     * Icon shown on the left of the Nav Item
     */
    icon?: IconType | ReactElement;
    /**
     * Text of the Nav Item
     */
    text: string;
    /**
     * Used to fill the right badge and specify outstanding tasks.
     */
    actionCount?: number;
    /**
     * Child Elements in the navigation tree
     */
    subItems?: NavItemProps[];
    /**
     * Additional CSS Override
     */
    className?: string;
    /**
     * Should the link be opened in a new window/tab
     */
    isExternal?: boolean;
    /**
     * Different Nav Items behave visually differently
     * @default "main"
     */
    kind?: NavItemKind;
    /**
     * Link to navigate to in this nav item
     */
    url: string;
    /**
     * Callback function that is called when the collapsible expands and collapses
     */
    onToggle?: (isExpanded: boolean) => void;
    /**
     * Override Other Logic to force a specific state if the item is Collapsible.
     *
     * @default "Default"
     */
    expandedState?: ExpandedState;
    /**
     * For accessability reasons we sometimes want to append additional contextual information for screen readers.
     */
    accessabilityAppend?: string;
    /**
     * Callback function that is called when the NavItem is clicked
     */
    onClick?: () => void;
};

const AngledLine: React.FC = () => {
    return (
        <svg
            aria-hidden={true}
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="20"
            viewBox="0 0 17 21"
            fill="none"
            style={
                {
                    width: "16px",
                    height: "20px",
                    strokeWidth: "1.25px",
                    stroke: "var(--s-color-separator-standard)",
                } as CSSProperties
            }
        >
            <path
                d="M1 0V12C1 16.4183 4.58172 20 9 20H17"
                stroke="#D1D3D1"
                strokeWidth="1.25"
            />
        </svg>
    );
};

const Chevron: React.FC = () => (
    <FiChevronDown
        style={{
            width: "20px",
            height: "24px",
        }}
        className={styles.chevron}
        aria-hidden="true"
    />
);

export const NavItem: React.FC<NavItemProps> = (props) => {
    const {
        icon,
        text,
        actionCount,
        subItems: originalSubItems,
        className,
        url,
        kind = "main",
        isExternal = false,
        expandedState = "Default",
        onToggle,
        accessabilityAppend,
        onClick,
    } = props;

    const {
        isSelected,
        defaultExpanded,
        isExpanded,
        setIsExpanded,
        alwaysExpanded,
        isCollapsible,
        subItems,
    } = useNavItem(
        url,
        kind,
        text,
        originalSubItems || [],
        expandedState,
        accessabilityAppend,
    );

    const right = actionCount ? (
        <Badge
            className={cn(
                { [styles["badge-visible"]]: !isExpanded || !isCollapsible },
                { [styles["badge-hidden"]]: isExpanded && isCollapsible },
            )}
            count={actionCount}
            title={`${actionCount} Tasks`}
        />
    ) : null;
    // This needs to be hard set on the `svg` element to override the behavior in the `menu.scss`
    const left = icon
        ? "type" in icon
            ? icon
            : createElement(icon, {
                  style: {
                      width: "20px",
                      height: "20px",
                      padding: "0 var(--g-spacing-xxs)",
                  } as CSSProperties,
                  "aria-hidden": true,
              })
        : null;

    const buttonClassName = cn(
        className,
        styles["nav-item-button"],
        styles[`nav-item-button--${kind}`],
        {
            [styles["nav-item-button--selected"]]: isSelected,
        },
    );

    const menuItemClassName = cn(
        styles["nav-item-title"],
        styles[`nav-item-title--${kind}`],
        {
            [styles["nav-item-title--static"]]: !subItems,
        },
    );

    const titleId = useId();

    const body = (
        <MenuItem
            id={titleId}
            tag={Link}
            wrapperTag="span"
            className={menuItemClassName}
            adornLeft={left}
            href={url}
            rel={isExternal ? "noopener noreferrer" : undefined}
            target={isExternal ? "_blank" : undefined}
            adornRight={right}
            justifyAdornRight="right"
            aria-current={isSelected ? "page" : undefined}
            onClick={onClick}
        >
            <span
                className={cn({
                    [styles["nav-item-text--selected"]]: isSelected,
                })}
                aria-label={
                    accessabilityAppend
                        ? `${text} ${accessabilityAppend}`
                        : undefined
                }
            >
                {text}
            </span>
        </MenuItem>
    );

    const subItemClass = cn(styles["nav-item-button--sub-item"], {
        [styles["nav-item--always-shown-sub-item"]]: alwaysExpanded,
    });

    const inner = subItems.length ? (
        <ul
            className={cn(styles["sub-items"], styles[`sub-items--${kind}`])}
            aria-labelledby={!isCollapsible ? titleId : undefined}
        >
            {subItems.map((item) => (
                <NavItem
                    key={item.url}
                    {...item}
                    kind={childKind(kind)}
                    className={subItemClass}
                    onClick={item.onClick}
                />
            ))}
        </ul>
    ) : null;

    const onToggleFunc = isCollapsible
        ? (isExpanding: boolean) => {
              setIsExpanded(isExpanding);
              onToggle?.(isExpanding);
          }
        : undefined;

    return (
        <li className="nav-item">
            {isCollapsible ? (
                <Collapsible
                    headerColumnGap={"0"}
                    headerClassName={buttonClassName}
                    defaultExpanded={defaultExpanded}
                    expanded={isExpanded}
                    buttonMode={{ accessibleTitle: `Menu: ${text}` }}
                    icon={<Chevron />}
                    title={body}
                    hidePadding={true}
                    hideBottomBorder={true}
                    hideTopBorder={true}
                    onToggle={onToggleFunc}
                >
                    {inner}
                </Collapsible>
            ) : kind === "sub-page" ? (
                <div className={styles["sub-page-flex"]}>
                    <AngledLine />
                    <div className={buttonClassName}>{body}</div>
                </div>
            ) : (
                <>
                    <div className={buttonClassName}>{body}</div>
                    {inner}
                </>
            )}
        </li>
    );
};
